/** Normalize warehouse `candidates[]` from GET /onboarding/warehouse for UI rows. */

import type {
  OnboardingWarehouseBlock,
  OnboardingWarehouseCandidate,
} from "@/types/onboarding.types";

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const s = String(value).trim();
  return s ? s : null;
}

/**
 * Parse raw BFF candidate objects into a stable shape for list rendering.
 */
/** True when the BFF sent two or more distinct BigQuery destination rows. */
export function hasMultipleWarehouseCandidates(
  candidates: OnboardingWarehouseCandidate[] | null | undefined,
): boolean {
  // Deleted warehouses stay in Daton's list; they are not destinations to choose between.
  return (candidates ?? []).filter((candidate) => !isWarehouseCandidateDeleted(candidate)).length > 1;
}

/**
 * Coerce a warehouse block's loose ``candidates`` JSON into typed rows. Call it once where the
 * block enters the app (the API layer), so everything downstream can rely on the type.
 */
export function normalizeWarehouseBlock(block: OnboardingWarehouseBlock): OnboardingWarehouseBlock {
  if (!Array.isArray(block.candidates)) {
    return block;
  }
  return {
    ...block,
    candidates: normalizeWarehouseCandidates(block.candidates as unknown as Record<string, unknown>[]),
  };
}

export function normalizeWarehouseCandidates(
  raw: Record<string, unknown>[] | null | undefined,
): OnboardingWarehouseCandidate[] {
  if (!raw?.length) {
    return [];
  }
  return raw.map((row) => ({
    warehouseId: asNumber(row.warehouseId),
    projectId: asString(row.projectId),
    dataset: asString(row.dataset),
    status: asString(row.status),
    region: asString(row.region),
    datawarehouseId: asNumber(row.datawarehouseId),
    count: asNumber(row.count),
    enableNewPlatform: row.enableNewPlatform === true,
  }));
}

/**
 * Primary label for a warehouse row (dataset name, else warehouse id).
 */
export function warehouseCandidateTitle(candidate: OnboardingWarehouseCandidate): string {
  if (candidate.dataset) {
    return candidate.dataset;
  }
  if (candidate.warehouseId != null) {
    return `Warehouse ${candidate.warehouseId}`;
  }
  return "BigQuery destination";
}

/**
 * Secondary line: project, status, region, counts — omit empty parts.
 */
export function warehouseCandidateSubtitle(
  candidate: OnboardingWarehouseCandidate,
): string {
  const parts: string[] = [];
  if (candidate.projectId) {
    parts.push(candidate.projectId);
  }
  if (candidate.status) {
    parts.push(candidate.status);
  }
  if (candidate.region) {
    parts.push(candidate.region);
  }
  if (candidate.count != null && candidate.count > 0) {
    parts.push(String(candidate.count));
  }
  return parts.join(" · ");
}

/** Daton keeps deleted warehouses in the list with status DELETED; they take no actions. */
export function isWarehouseCandidateDeleted(
  candidate: OnboardingWarehouseCandidate,
): boolean {
  return (candidate.status ?? "").trim().toUpperCase() === "DELETED";
}

export function isWarehouseCandidateEnabled(
  candidate: OnboardingWarehouseCandidate,
): boolean {
  return (candidate.status ?? "").trim().toUpperCase() === "ENABLED";
}
